import { Button, Flex, FlexProps, Spinner, Text } from '@chakra-ui/react';
import { queryClient } from '@shared/config';
import { links, maskDocument, maskTelephone } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  QUERY_KEY_GET_REQUEST_ALL_INFO,
  useCreateCrossSeller,
  useGetRequestAllInfo,
  useGetUserInfo,
} from '../../hooks';

const cardStyle: FlexProps = {
  flexDir: 'column',
  bg: 'brand.50',
  borderRadius: 'lg',
  p: 5,
};

export const SummaryPurchaseData = () => {
  const { t } = useTranslation('cross-seller/summary');
  const navigate = useNavigate();
  const { data, isFindingAllInfo } = useGetRequestAllInfo();
  const { data: userInfo } = useGetUserInfo();
  const { mutate: createCrossSeller, isLoading: isCreatingCrossSeller } =
    useCreateCrossSeller();

  const companyId = sessionStorage.getItem('companyId') as string;
  const currentCompany = userInfo?.UserCompany?.find(
    company => company.companyId === companyId,
  );

  const handleCreateCrossSeller = () => {
    createCrossSeller(
      {
        companyId,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(QUERY_KEY_GET_REQUEST_ALL_INFO);

          // requestId
          localStorage.removeItem('requestId');
          sessionStorage.removeItem('requestId');

          // remove all session infos
          sessionStorage.clear();

          navigate(links.crossSeller.success);
        },
      },
    );
  };

  if (isFindingAllInfo)
    return (
      <Flex
        gap={4}
        h="100dvh"
        maxH="calc(100dvh - 210px)"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );

  return (
    <Flex flexDir="column" gap={4}>
      <Flex gap={4} h="100dvh" maxH="calc(100dvh - 162px)" overflowY="auto">
        <Flex flexDir="column" gap={4} w="full">
          <Text
            color="brand.800"
            fontSize="2xl"
            fontWeight={600}
            textAlign={{ base: 'center', lg: 'start' }}
          >
            {t('data')}
          </Text>
          <Flex {...cardStyle}>
            <Flex
              flexDir={{ base: 'column-reverse', lg: 'row' }}
              justifyContent={{ lg: 'space-between' }}
              alignItems={{ base: 'start', lg: 'center' }}
              mb={4}
            >
              <Text color="brand.800" fontSize="lg" fontWeight="semibold">
                {currentCompany?.Company.companyName}
              </Text>
            </Flex>

            <Text color="brand.800" fontSize="sm" fontWeight="medium">
              {t('cnpj')}:{' '}
              {maskDocument(currentCompany?.Company.documentNumber ?? '')}
            </Text>
            <Text color="brand.800" fontSize="sm" fontWeight="medium">
              {t('phone')}:{' '}
              {maskTelephone(currentCompany?.Company.phones?.[0] ?? '')}
            </Text>
          </Flex>

          <Text
            color="brand.800"
            fontSize="2xl"
            fontWeight={600}
            textAlign={{ base: 'center', lg: 'start' }}
          >
            {t('paymentType')}
          </Text>
          <Flex {...cardStyle}>
            <Text color="brand.800" fontSize="lg" fontWeight="semibold">
              {t('cardInfo', {
                brand: data?.requestPayments[0]?.brand,
                cardNumber: data?.requestPayments[0]?.lastFourNumbers,
              })}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Flex justifyContent="space-between">
        <Button
          as={Link}
          to={links.crossSeller.payment}
          colorScheme="brand"
          variant="outline"
        >
          {t('common:previous')}
        </Button>

        <Button
          as={Link}
          to={links.crossSeller.summary}
          colorScheme="brand"
          w={{ base: 140, lg: 240 }}
          onClick={handleCreateCrossSeller}
          isLoading={isCreatingCrossSeller}
        >
          {t('common:continue')}
        </Button>
      </Flex>
    </Flex>
  );
};
